import { Component, EventEmitter, Input, Output } from "@angular/core";
import { SubjectType } from "@core/interfaces/image.interface";
import { ImageService } from "@core/services/image/image.service";
import { SubjectTypeSuggestionInterface } from "@features/image/services/subject-type-suggestion.interface";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "astrobin-subject-type-suggestion-pill",
  templateUrl: "./subject-type-suggestion-pill.component.html",
  styleUrls: ["./subject-type-suggestion-pill.component.scss"]
})
export class SubjectTypeSuggestionPillComponent {
  @Input() suggestion: SubjectTypeSuggestionInterface;
  @Input() loading = false;
  @Input() isSolarSystemSubject = false;
  @Output() apply = new EventEmitter<SubjectType | string>();
  @Output() dismiss = new EventEmitter<void>();

  constructor(
    public readonly translateService: TranslateService,
    public readonly imageService: ImageService
  ) {}

  get confidenceLevel(): "high" | "medium" | "low" | "very-low" {
    if (!this.suggestion) {
      return "low";
    }
    const confidence = this.suggestion.confidence * 100;
    if (confidence >= 80) {
      return "high";
    }
    if (confidence >= 60) {
      return "medium";
    }
    if (confidence >= 40) {
      return "low";
    }
    return "very-low";
  }

  get confidencePercentage(): number {
    if (!this.suggestion) {
      return 0;
    }
    return Math.round(this.suggestion.confidence * 100);
  }

  get displayValue(): string {
    if (!this.suggestion) {
      return "";
    }
    if (this.isSolarSystemSubject && this.suggestion.solarSystemMainSubject) {
      return this.imageService.humanizeSolarSystemSubjectType(this.suggestion.solarSystemMainSubject);
    }
    return this.imageService.humanizeSubjectType(this.suggestion.subjectType);
  }

  get tooltipText(): string {
    if (!this.suggestion) {
      return "";
    }
    const confidenceText = this.translateService.instant("Suggested with {{confidence}}% confidence.", {
      confidence: this.confidencePercentage
    });

    if (this.suggestion.explanation) {
      return `${confidenceText} ${this.suggestion.explanation}`;
    }
    return confidenceText;
  }

  onApply(): void {
    if (this.isSolarSystemSubject && this.suggestion.solarSystemMainSubject) {
      this.apply.emit(this.suggestion.solarSystemMainSubject);
    } else {
      this.apply.emit(this.suggestion.subjectType);
    }
  }

  onDismiss(): void {
    this.dismiss.emit();
  }
}
